import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTrainingAdminDashboard, GetTrainingAdminDashboardOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { BookOpen, Upload, Shield, ClipboardCheck, AlertTriangle, ArrowRight, Eye, Edit, Users, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Props = { onNavigate: (section: string) => void };

export default function DashboardSection({ onNavigate }: Props) {
  const [data, setData] = useState<GetTrainingAdminDashboardOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getTrainingAdminDashboard({})
      .then(setData)
      .catch(e => setError(e?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-40" /><Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-3" />
        <h3 className="font-semibold text-foreground mb-1">Failed to load dashboard</h3>
        <p className="text-sm text-muted-foreground mb-3">{error}</p>
        <Button size="sm" variant="outline" onClick={() => { setError(''); setLoading(true); getTrainingAdminDashboard({}).then(setData).catch(e => setError(e?.message || 'Failed')).finally(() => setLoading(false)); }}>
          Retry
        </Button>
      </div>
    );
  }

  if (!data) return null;
  const { stats, attentionItems, recentCourses } = data;

  const statCards = [
    { label: 'Draft Courses', value: stats.draftCourses, icon: BookOpen, onClick: () => onNavigate('courses') },
    { label: 'Published Courses', value: stats.publishedCourses, icon: Layers, onClick: () => onNavigate('courses') },
    { label: 'Total Modules', value: stats.totalModules, icon: Upload, onClick: () => onNavigate('modules') },
    { label: 'Assessments Waiting', value: stats.assessmentsWaiting, icon: ClipboardCheck, onClick: () => onNavigate('assessments') },
  ];

  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Training Dashboard</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {statCards.map(s => (
          <button key={s.label} onClick={s.onClick} className="border border-border rounded-lg p-4 bg-card text-left hover:border-primary/30 transition-colors group">
            <div className="flex items-center justify-between mb-2">
              <s.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="text-2xl font-bold text-foreground">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Attention items */}
        <div className="border border-border rounded-lg p-4 bg-card">
          <p className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive" /> Requires Attention
          </p>
          {attentionItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing needs attention right now ✓</p>
          ) : (
            <div className="space-y-2">
              {attentionItems.map((item, i) => (
                <button key={i} onClick={() => onNavigate(item.type)} className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left block">
                  • {item.text}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent courses with actions */}
        <div className="border border-border rounded-lg p-4 bg-card">
          <p className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary" /> Courses
          </p>
          {recentCourses.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-2">No courses created yet</p>
              <Button size="sm" onClick={() => onNavigate('courses')}>Create First Course</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCourses.slice(0, 5).map(c => (
                <div key={c.id} className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{c.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{c.moduleCount} modules</span>
                      <span className={`px-1.5 py-0.5 rounded ${c.status === 'Published' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {c.status || 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link to={`/train/admin/preview/${c.id}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Preview">
                      <Eye className="w-3.5 h-3.5" />
                    </Link>
                    <button onClick={() => onNavigate('courses')} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Edit">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <p className="font-semibold text-sm text-foreground mb-3">Quick Actions</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button onClick={() => onNavigate('courses')} className="p-3 border border-border rounded-lg text-left hover:border-primary/30 transition-colors">
            <BookOpen className="w-4 h-4 text-muted-foreground mb-1" />
            <p className="text-xs font-medium text-foreground">Manage Courses</p>
          </button>
          <button onClick={() => onNavigate('modules')} className="p-3 border border-border rounded-lg text-left hover:border-primary/30 transition-colors">
            <Layers className="w-4 h-4 text-muted-foreground mb-1" />
            <p className="text-xs font-medium text-foreground">Modules & Lessons</p>
          </button>
          <button onClick={() => onNavigate('policies')} className="p-3 border border-border rounded-lg text-left hover:border-primary/30 transition-colors">
            <Shield className="w-4 h-4 text-muted-foreground mb-1" />
            <p className="text-xs font-medium text-foreground">Policies</p>
          </button>
          <button onClick={() => onNavigate('missing-content')} className="p-3 border border-border rounded-lg text-left hover:border-primary/30 transition-colors">
            <AlertTriangle className="w-4 h-4 text-muted-foreground mb-1" />
            <p className="text-xs font-medium text-foreground">Missing Content</p>
          </button>
        </div>
      </div>
    </div>
  );
}
